import { FirebaseUser, ResponseError } from "avvir";

import getUser from "../getters/base_getters/get_user";
import loadImage from "../component_helpers/canvas_helpers/load_image";
import { GetState } from "type_aliases";

export default class ImageFetcher {
  getState: GetState;

  constructor(getState) {
    this.getState = getState;
  }

  getUrlAsImage = (url: string): Promise<HTMLImageElement> => {
    return this.getBlobUrl(url)
      .then(loadImage)
      .then(event => event.currentTarget);
  };

  getBlobUrl = (url: string) => {
    // A firebase download url carries its own credential in `token`, so it needs no authorization header.
    // Sending one anyway makes storage answer with a redirect to a signed storage.googleapis.com url, and a
    // cross-origin redirect makes the browser send `Origin: null`, which matches no bucket CORS rule.
    if (url.startsWith("https://firebasestorage.googleapis") && !hasDownloadToken(url)) {
      return this.getBlobUrlWithAuth(url);
    } else {
      return this.getBlobUrlNoAuth(url);
    }
  };

  getBlobUrlWithAuth = async (url: string): Promise<string> => {
    const authToken = (getUser(this.getState(), {}) as FirebaseUser).firebaseUser.idToken;
    const response = await fetch(url, {
      headers: {
        authorization: `Firebase ${authToken}`
      }
    });

    if (!response.ok) {
      throw new ResponseError("Failed to fetch image from storage", "", response, {});
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  };

  getBlobUrlNoAuth = async (url: string): Promise<string> => {
    const response = await fetch(url);
    if (!response.ok) {
      throw new ResponseError("Failed to fetch image from 3rd party", "", response, {});
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  };
}

const hasDownloadToken = (url: string): boolean => {
  try {
    return !!new URL(url).searchParams.get("token");
  } catch {
    return false;
  }
};
